import {
  faAngleDown,
  faArrowsRotate,
  faBookmark,
  faEllipsis,
  faHeart,
  faPen,
  faPlus,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'src/components/Alert';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Chip } from 'src/components/Chip';
import { ConfirmDialog } from 'src/components/ConfirmDialog';
import { Dialog } from 'src/components/Dialog';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { ListItem } from 'src/components/ListItem';
import { Scroller } from 'src/components/Scroller';
import { SearchTextField } from 'src/components/SearchTextField';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { QuickLink, deleteQuickLink } from 'src/models/quickLink/quickLink';
import {
  addQuickLink,
  fetchQuickLinks,
  quickLinkTypes,
  updateQuickLink,
} from 'src/models/quickLink/quickLink';
import { updateUser } from 'src/models/user/user';
import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import toast from 'src/utils/toast';
import { useListSearchParams } from 'src/utils/useListSearchParams';

import { NoResults } from '../NoResults';


type useListSearchParamsReturn = ReturnType<typeof useListSearchParams>;
interface QuickLinksProps {
  onApply: (quickLink: any) => void;
  onUpdateWithCurrentFilters?: (quickLink: any) => void;
  type?: quickLinkTypes;
  linkerId?: Identifier;
  showDefaultButton?: boolean;
  params: useListSearchParamsReturn['params'];
  filterParams: useListSearchParamsReturn['filterParams'];
  getQuickLinkString: () => string;
}

export const QuickLinks: React.FC<QuickLinksProps> = ({
  onApply,
  onUpdateWithCurrentFilters,
  type = 'clients',
  linkerId,
  showDefaultButton = true,
  params,
  filterParams,
  getQuickLinkString,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'filters.quickLinks',
  });
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });
  const { session, user } = useSession();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState('');
  const [newViewDialogOpen, setNewViewDialogOpen] = useState(false);
  const [newViewName, setNewViewName] = useState('');

  const [viewToEdit, setViewToEdit] = useState<QuickLink | null>(null);
  const [viewToDelete, setViewToDelete] = useState<QuickLink | null>(null);

  const { data: links } = useQuery({
    queryKey: ['quickLinks', type, linkerId],
    queryFn: () => {
      return fetchQuickLinks({ type: type, linkerId });
    },
  });

  const deleteQuickLinkMutation = useMutation({
    mutationFn: () => {
      if (!viewToDelete?.id) {
        throw new Error('Deleted view without id');
      }
      return deleteQuickLink({
        id: viewToDelete?.id,
        type,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['quickLinks', type],
      });
      toast.success('View deleted');
      setViewToDelete(null);
    },
    onError: () => {
      toast.error('View could not be deleted');
    },
  });

  const updateQuickLinkMutation = useMutation({
    mutationFn: () => {
      if (!viewToEdit?.id) {
        throw new Error('Edited view without id');
      }
      return updateQuickLink({
        id: viewToEdit?.id,
        type,
        updatedQuickLink: {
          name: viewToEdit.name,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['quickLinks', type],
      });
      toast.success('View renamed successfully');
      setViewToEdit(null);
    },
    onError: () => {
      toast.error('View could not be renamed');
    },
  });

  const addQuickLinkMutation = useMutation({
    mutationFn: () => {
      const filtersAsString = getQuickLinkString();
      return addQuickLink({
        type,
        linkerId: linkerId || session.id,
        name: newViewName,
        filters: filtersAsString,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['quickLinks', type],
      });
      toast.success('View created successfully');
      setNewViewDialogOpen(false);
      setNewViewName('');
    },
    onError: () => {
      toast.error('View could not be created');
      setNewViewDialogOpen(false);
      setNewViewName('');
    },
  });

  const updateViewToEditName = (e) => {
    if (!viewToEdit?.id) {
      return;
    }
    setViewToEdit({ ...viewToEdit, name: e.target.value });
  };

  const filteredLinks = (links || []).filter((link) => {
    return link.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const currentFilters = useMemo(
    () => ({
      tags: (filterParams?.tags?.values || []).map(String),
      phase: String(filterParams?.phase || ''),
      planId: String(filterParams?.workoutPlan || ''),
      search: String(params.search || ''),
      filterNum: String(filterParams?.filterNum || ''),
      tagBoolean: filterParams?.tags?.matchAll ? 'all' : 'any',
      clientStatus: String(filterParams?.status || ''),
      customStatus: String(filterParams?.customStatus?.value || ''),
      lifecycleIds: String(filterParams?.lifecycleStage || ''),
      profileField: String(filterParams?.customProfileField?.field || ''),
      subTrainerId: String(filterParams?.trainer || ''),
      customGroupId: String(filterParams?.group || ''),
      profileFieldSearch: String(filterParams?.customProfileField?.value || ''),
    }),
    [filterParams, params.search]
  );

  const sortObjectKeys = (obj: object) => {
    return Object.fromEntries(
      Object.entries(obj).sort(([a], [b]) => a.localeCompare(b))
    );
  };

  const showSaveDefaultButton = useMemo(() => {
    return (
      type === 'clients' &&
      JSON.stringify(sortObjectKeys(currentFilters)) !==
        JSON.stringify(sortObjectKeys(user.defaultClientFilters || {}))
    );
  }, [currentFilters, user.defaultClientFilters, type]);

  const handleSaveDefaultFilters = async () => {
    try {
      await updateUser(user.id, {
        defaultClientFilters: currentFilters,
      });
      queryClient.invalidateQueries({
        queryKey: ['currentUser'],
      });
      toast.success('Default filters updated');
    } catch (error) {
      toast.error(api.formatError(error, 'Failed to update default filters'));
    }
  };

  return (
    <>
      <DropdownMenu
        trigger={
          <Chip
            sx={{
              '.MuiChip-label': {
                p: 0.6,
              },
            }}
            label={
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Icon
                  size="small"
                  icon={faHeart}
                />
                <Icon
                  size="small"
                  sx={{ color: 'text.secondary' }}
                  icon={faAngleDown}
                />
              </Stack>
            }
            variant="outlined"
          />
        }
      >
        <Stack spacing={1} sx={{ minWidth: '310px', p: 1 }}>
          {showDefaultButton && showSaveDefaultButton && (
            <ListItem
              primary={t('useCurrentFiltersAsDefault')}
              clickable
              rounded
              icon={faBookmark}
              onClick={handleSaveDefaultFilters}
            />
          )}
          <Box onClick={(e) => e.stopPropagation()}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ py: 1, pl: 1 }}
            >
              <Typography variant="h5">{t('views')}</Typography>
              <Stack direction="row">
                <IconButton onClick={() => setNewViewDialogOpen(true)}>
                  <Icon icon={faPlus} />
                </IconButton>
              </Stack>
            </Stack>
            <Box sx={{ px: 1, pb: 1 }}>
              <SearchTextField
                value={searchTerm}
                onSearch={setSearchTerm}
                placeholder={t('searchViews')}
                onCancel={() => {
                  setSearchTerm('');
                }}
              />
            </Box>
          </Box>
          {!filteredLinks.length && !!searchTerm && (
            <NoResults compact sx={{ pb: 0 }} />
          )}
          {!filteredLinks.length && !searchTerm && (
            <NoResults title={t('noViewsConfigured')} compact>
              <Button
                variant="contained"
                onClick={() => setNewViewDialogOpen(true)}
              >
                {t('createView')}
              </Button>
            </NoResults>
          )}
          {!!filteredLinks.length && (
            <Scroller direction="column" sx={{ maxHeight: '40svh' }}>
              {filteredLinks?.map((link) => {
                return (
                  <ListItem
                    rounded
                    clickable
                    key={link.name + link.filters}
                    primary={link.name}
                    onClick={() => onApply(link)}
                    sx={{ pr: 1 }}
                    rightAdornment={
                      <Box onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu
                          trigger={
                            <IconButton>
                              <Icon
                                size="small"
                                icon={faEllipsis}
                              />
                            </IconButton>
                          }
                        >
                          <Stack sx={{ p: 1 }} spacing={1}>
                            <ListItem
                              primary={t('setToCurrentFiltersAndSearch')}
                              clickable
                              rounded
                              icon={faArrowsRotate}
                              onClick={() =>
                                onUpdateWithCurrentFilters
                                  ? onUpdateWithCurrentFilters(link)
                                  : onApply(link)
                              }
                            />
                            <ListItem
                              primary={t('rename')}
                              clickable
                              rounded
                              onClick={() => setViewToEdit(link)}
                              icon={faPen}
                            />
                            <ListItem
                              primary={tCommon('actions.delete')}
                              clickable
                              rounded
                              onClick={() => setViewToDelete(link)}
                              icon={faTrash}
                            />
                          </Stack>
                        </DropdownMenu>
                      </Box>
                    }
                  />
                );
              })}
            </Scroller>
          )}
        </Stack>
      </DropdownMenu>
      <ConfirmDialog
        open={!!viewToDelete}
        actions={[
          {
            label: tCommon('buttons.cancel'),
            color: 'secondary',
            onClick: () => {
              setViewToDelete(null);
            },
          },
          {
            label: tCommon('actions.delete'),
            color: 'error',
            variant: 'contained',
            onClick: () => {
              deleteQuickLinkMutation.mutate();
            },
          },
        ]}
        title={t('areYouSure')}
      >
        {t('permanentlyRemoveView', { name: viewToDelete?.name })}
      </ConfirmDialog>
      <Dialog
        title={t('createView')}
        open={newViewDialogOpen}
        onClose={() => setNewViewDialogOpen(false)}
        actions={[
          {
            label: tCommon('buttons.cancel'),
            disabled: addQuickLinkMutation.isPending,
            onClick: () => {
              setNewViewDialogOpen(false);
              setNewViewName('');
            },
          },
          {
            label: tCommon('buttons.save'),
            variant: 'contained',
            disabled: !newViewName,
            loading: addQuickLinkMutation.isPending,
            onClick: () => addQuickLinkMutation.mutate(),
          },
        ]}
      >
        <Alert>{t('savesCurrentFiltersAndSearch')}</Alert>
        <TextField
          label={t('name')}
          value={newViewName}
          onChange={(e) => setNewViewName(e.target.value)}
          sx={{ mt: 2 }}
        ></TextField>
      </Dialog>
      <Dialog
        title={t('renameView')}
        open={!!viewToEdit}
        onClose={() => setViewToEdit(null)}
        actions={[
          {
            label: tCommon('buttons.cancel'),
            disabled: updateQuickLinkMutation.isPending,
            onClick: () => setViewToEdit(null),
          },
          {
            label: tCommon('buttons.save'),
            variant: 'contained',
            disabled: !viewToEdit?.name,
            loading: updateQuickLinkMutation.isPending,
            onClick: () => updateQuickLinkMutation.mutate(),
          },
        ]}
      >
        <TextField
          label={t('name')}
          value={viewToEdit?.name}
          onChange={updateViewToEditName}
          sx={{ mb: 2 }}
        ></TextField>
      </Dialog>
      <Dialog
        title={t('createView')}
        open={newViewDialogOpen}
        onClose={() => setNewViewDialogOpen(false)}
        actions={[
          {
            label: tCommon('buttons.cancel'),
            disabled: addQuickLinkMutation.isPending,
            onClick: () => setNewViewDialogOpen(false),
          },
          {
            label: tCommon('buttons.save'),
            variant: 'contained',
            disabled: !newViewName,
            loading: addQuickLinkMutation.isPending,
            onClick: () => addQuickLinkMutation.mutate(),
          },
        ]}
      >
        <Alert>{t('savesCurrentFiltersAndSearch')}</Alert>
        <TextField
          label={t('name')}
          value={newViewName}
          onChange={(e) => setNewViewName(e.target.value)}
          sx={{ mt: 2 }}
        ></TextField>
      </Dialog>
    </>
  );
};
